import { createAuth, AuthState } from '@oscar/auth';
import { Loading } from 'common/components';

export const Roles = ['ADMIN', 'OSCAR', 'INSULATION', 'CONTRACTOR', 'PAINTING'];
export const ROLES_NAME: Record<string, string> = {
  ADMIN: 'ADMINISTRATOR',
  OSCAR: 'REQUESTOR',
  INSULATION: 'INSULATION TEAM',
  PAINTING: 'PAINTING TEAM',
  CONTRACTOR: 'CONTRACTOR',
};

type Permission = [
  'EDITED',
  'CANCELLED',
  'REJECTED',
  'CLOSED',
  'COMPLETED',
  'CREATED',
  'SUBMITTED',
  'SAVED',
  'SENDBACK',
];
type Policies = {
  ADMIN: [
    'REQUEST_DRAFF',
    'REQUEST_INSERVICE',
    'REQUEST_PENDING',
    'REQUEST_PROPOSAL',
    'REQUEST',
    'REQUEST_REMOVAL',
    'REQUEST_REINSTATEMENT',
    'REQUEST_BILLING',
    'PENDING_PAINTING',
    'CLOSED',

    // 'PENDING_REMOVAL',
    // 'PENDING_REINSTATEMENT',
  ];
  OSCAR: [
    'REQUEST_DRAFF',
    'PENDING_VALIDTION',
    'REQUEST_PROPOSAL',
    'REQUEST_INSERVICE',
    'DRAFT',
    'PENDING_QAQC',
    'PENDING_PROPOSAL',
    'IN_SERVICE',
  ];
  INSULATION: [
    'REQUEST_VALIDATION',
    'REQUEST_BILLING',
    'PENDING_VALIDTION',
    'PENDING_QAQC',
    'PENDING_QAQC_1',
    'PENDING_QAQC_2',
    'PENDING_QAQC_3',
    'PENDING_QAQC_4',
    'PENDING_QAQC_5',
    'BILLING',
    'PENDING_REMOVAL',
    'PENDING_REINSTATEMENT',
  ];
  PAINTING: [
    'REQUEST_VALIDATION',
    'REQUEST_BILLING',
    'PENDING_VALIDTION',
    'PENDING_QAQC',
    'PENDING_QAQC_1',
    'PENDING_QAQC_2',
    'PENDING_QAQC_3',
    'PENDING_QAQC_4',
    'PENDING_QAQC_5',
    'BILLING',
    'PENDING_PAINTING',
  ];
  CONTRACTOR: [
    'REQUEST_PROPOSAL',
    'REQUEST_REMOVAL',
    'PENDING_PROPOSAL',
    'REQUEST_REINSTATEMENT',
    'PENDING_REMOVAL',
    'PENDING_REINSTATEMENT',
    'PENDING_PAINTING',
    'PENDING_PAINTING_1',
    'PENDING_PAINTING_2',
    'PENDING_PAINTING_3',
    'PENDING_PAINTING_4',
    'PENDING_PAINTING_5',
  ];
};

const { AuthProvider, useAuth } = createAuth<AuthState<Permission, Policies>>({
  switchRole: true,
  loading: <Loading />,
  provider: {
    SSO: {
      type: 'MSA',
      validateAuthority: true,
      clientId: process.env.REACT_APP_MSAL_CLIENT_ID!,
      authority: process.env.REACT_APP_MSAL_AUTHORITY!,
      redirectUri: process.env.REACT_APP_MSAL_REDIRECT_URI!,
      postLogoutRedirectUri:
        process.env.REACT_APP_MSAL_POST_LOGOUT_REDIRECT_URI!,
      scopes: ['openid', 'profile', 'User.Read'],
      mode: 'redirect',
    },
  },
  getUserRole: (value: any) => {
    if (!value?.role) {
      return [];
    }
    if (value?.role instanceof Array) {
      return value?.role
        ?.map((item: string) => {
          return Roles[parseInt(item) - 1] as keyof Policies;
        })
        .filter((item: string) => item);
    } else {
      const role = Roles[parseInt(value?.role) - 1] as keyof Policies;
      return [role];
    }
  },
  getUserInformation: value => {
    return {
      fullName: value?.FullName,
      position: value?.Position,
      department: value?.Department,
      shortName: getShorName(value?.FullName),
      serviceProviderName: value?.ServiceProviderName,
    };
  },
  getPermission: () => {
    return [
      {
        name: 'ADMIN',
        permissions: {
          REQUEST_BILLING: ['SUBMITTED', 'SAVED'],
          REQUEST: ['COMPLETED', 'CLOSED'],
          REQUEST_PROPOSAL: ['SUBMITTED', 'CREATED', 'REJECTED'],
          REQUEST_REMOVAL: ['EDITED'],
          REQUEST_REINSTATEMENT: ['EDITED'],
          PENDING_PAINTING: ['SAVED', 'SUBMITTED', 'EDITED'],
          PENDING_REMOVAL: ['SUBMITTED', 'CANCELLED'],
          PENDING_REINSTATEMENT: ['CANCELLED', 'SUBMITTED'],
          CLOSED: ['SENDBACK'],
        },
      },
      {
        name: 'OSCAR',
        permissions: {
          DRAFT: ['CREATED', 'SUBMITTED', 'EDITED', 'CANCELLED'],
          PENDING_VALIDTION: ['EDITED', 'CANCELLED'],
          REQUEST_DRAFF: ['SUBMITTED', 'EDITED', 'CANCELLED'],
          REQUEST_INSERVICE: ['COMPLETED'],
          PENDING_PROPOSAL: ['EDITED', 'CANCELLED'],
          PENDING_REMOVAL: ['CANCELLED'],
          PENDING_REINSTATEMENT: ['CANCELLED'],
          IN_SERVICE: ['EDITED'],
          PENDING_PAINTING: ['CANCELLED'],
          PENDING_QAQC: ['CANCELLED'],
        },
      },
      {
        name: 'INSULATION',
        permissions: {
          REQUEST_VALIDATION: ['REJECTED', 'SUBMITTED'],
          REQUEST_BILLING: ['SUBMITTED', 'SAVED'],
          PENDING_VALIDTION: ['REJECTED', 'SUBMITTED'],
          PENDING_QAQC: ['SAVED', 'SUBMITTED'],
          PENDING_QAQC_1: ['SUBMITTED'],
          PENDING_QAQC_2: ['SUBMITTED'],
          PENDING_QAQC_3: ['SUBMITTED'],
          PENDING_QAQC_4: ['SUBMITTED'],
          PENDING_QAQC_5: ['SUBMITTED'],
          BILLING: ['SUBMITTED', 'SAVED', 'CLOSED'],
          PENDING_REMOVAL: ['EDITED', 'SENDBACK'],
          PENDING_REINSTATEMENT: ['EDITED', 'SENDBACK'],
        },
      },
      {
        name: 'PAINTING',
        permissions: {
          REQUEST_VALIDATION: ['REJECTED', 'SUBMITTED'],
          REQUEST_BILLING: ['SUBMITTED', 'SAVED'],
          PENDING_VALIDTION: ['REJECTED', 'SUBMITTED'],
          PENDING_QAQC: ['SUBMITTED'],
          PENDING_QAQC_1: ['SUBMITTED'],
          PENDING_QAQC_2: ['SUBMITTED'],
          PENDING_QAQC_3: ['SUBMITTED'],
          PENDING_QAQC_4: ['SUBMITTED'],
          PENDING_QAQC_5: ['SUBMITTED'],
          BILLING: ['SUBMITTED', 'SAVED', 'CLOSED'],
          PENDING_PAINTING: ['SUBMITTED', 'SENDBACK'],
        },
      },
      {
        name: 'CONTRACTOR',
        permissions: {
          REQUEST_INSERVICE: ['EDITED'],
          REQUEST_PROPOSAL: ['SUBMITTED', 'CREATED', 'REJECTED'],
          REQUEST_REMOVAL: ['EDITED'],
          REQUEST_REINSTATEMENT: ['EDITED'],
          DRAFT: ['CANCELLED'],
          PENDING_PROPOSAL: ['SUBMITTED', 'CREATED', 'REJECTED', 'SENDBACK'],
          PENDING_REMOVAL: ['SUBMITTED'],
          PENDING_REINSTATEMENT: ['SUBMITTED'],
          PENDING_PAINTING: ['SAVED', 'SUBMITTED', 'EDITED'],
          PENDING_PAINTING_1: ['SAVED', 'SUBMITTED', 'EDITED'],
          PENDING_PAINTING_2: ['SAVED', 'SUBMITTED', 'EDITED'],
          PENDING_PAINTING_3: ['SAVED', 'SUBMITTED', 'EDITED'],
          PENDING_PAINTING_4: ['SAVED', 'SUBMITTED', 'EDITED'],
          PENDING_PAINTING_5: ['SAVED', 'SUBMITTED', 'EDITED'],
        },
      },
    ];
  },
});

const getShorName = (name: string) => {
  if (!name) {
    return 'G';
  }
  return name
    .replace('_', ' ')
    .split(' ')
    .map((item: string) => item.substring(0, 1))
    .join('')
    .toUpperCase();
};

export { AuthProvider, useAuth };
